import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Lock, CheckCircle, Package, Zap, Layers, MapPin } from "lucide-react";
import { ArrowRight } from "../components/DirectionalIcon";
import { toast } from "sonner";
import axios from "axios";
import logo from "../assets/logo.svg";
import { DEFAULT_LANGUAGE, LANGUAGE_STORAGE_KEY, isSupportedLanguage, applyDocumentDirection, buildLocalizedPath } from "../constants/languages";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TradePortalPage = () => {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState("gate"); // "gate" | "lot" | "success"
  const [buyerName, setBuyerName] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [lot, setLot] = useState(null);
  const [gateError, setGateError] = useState("");
  const [isCheckingCode, setIsCheckingCode] = useState(false);

  const [offerForm, setOfferForm] = useState({
    offer_amount: "",
    quantity_wanted: "",
    message: "",
  });
  const [isSubmittingOffer, setIsSubmittingOffer] = useState(false);

  useEffect(() => {
    document.title = t("portal.meta.title");
  }, [t]);

  // /portal sits outside the language-prefix system, so LanguageRedirect
  // deliberately skips it (see STANDALONE_ROUTES) — no "/tr/portal" exists to
  // send visitors to. That means the browser-language auto-detection that
  // other pages get via LanguageRedirect never runs here either. Restore just
  // the detection (not the navigation) for this route only: if the visitor
  // hasn't made an explicit choice yet, match their browser language and
  // switch in-memory. Never write to localStorage — only an actual
  // LanguageSwitcher click should ever be remembered as an explicit choice.
  useEffect(() => {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored) {
      // i18next already resolved i18n.language from this stored value at
      // init — just make sure dir/lang on <html> reflect it here too, since
      // LanguageWrapper (the only other caller) never runs on this route.
      applyDocumentDirection(i18n.language);
      return;
    }

    const browserLangs = navigator.languages && navigator.languages.length
      ? navigator.languages
      : [navigator.language];
    const match = browserLangs
      .map((l) => l.split("-")[0])
      .find((l) => l !== DEFAULT_LANGUAGE && isSupportedLanguage(l));

    if (match) {
      i18n.changeLanguage(match);
      applyDocumentDirection(match);
    } else {
      applyDocumentDirection(DEFAULT_LANGUAGE);
    }
  }, [i18n]);

  useEffect(() => {
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement("meta");
      metaRobots.name = "robots";
      document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute("content", "noindex, nofollow");

    let canonicalLink = document.querySelector("link[rel='canonical']");
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", "https://www.magalaxyintl.com/portal");
  }, []);

  const handleGateSubmit = async (e) => {
    e.preventDefault();
    setGateError("");
    setIsCheckingCode(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/portal/lots/${accessCode.trim()}`);
      setLot(res.data);
      setStep("lot");
    } catch {
      setGateError(t("portal.gate.error"));
    } finally {
      setIsCheckingCode(false);
    }
  };

  const handleOfferSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingOffer(true);
    try {
      await axios.post(`${BACKEND_URL}/api/portal/lots/${accessCode.trim()}/offers`, {
        buyer_name: buyerName,
        offer_amount: parseFloat(offerForm.offer_amount),
        quantity_wanted: offerForm.quantity_wanted ? parseInt(offerForm.quantity_wanted, 10) : undefined,
        message: offerForm.message.trim() ? offerForm.message.trim() : undefined,
      });
      setStep("success");
    } catch {
      toast.error(t("portal.offerForm.toastError"));
    } finally {
      setIsSubmittingOffer(false);
    }
  };

  const isOfferFormValid = () => buyerName.trim() !== "" && offerForm.offer_amount !== "";

  // ============== STEP 1: ACCESS GATE ==============
  if (step === "gate") {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md"
        >
          <div className="text-center mb-8">
            <img src={logo} alt="M A Galaxy International" className="h-14 mx-auto mb-4" loading="eager" />
            <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="text-gold" size={22} strokeWidth={1.5} />
            </div>
            <h1 className="font-outfit text-2xl font-bold text-navy">{t("portal.gate.title")}</h1>
            <p className="text-slate-500 mt-2 text-sm">
              {t("portal.gate.subtitle")}
            </p>
          </div>

          <form onSubmit={handleGateSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{t("portal.gate.nameLabel")}</label>
              <input
                type="text"
                value={buyerName}
                onChange={(e) => setBuyerName(e.target.value)}
                placeholder={t("portal.gate.namePlaceholder")}
                required
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold"
                data-testid="portal-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{t("portal.gate.codeLabel")}</label>
              <input
                type="text"
                value={accessCode}
                onChange={(e) => {
                  setAccessCode(e.target.value);
                  setGateError("");
                }}
                placeholder={t("portal.gate.codePlaceholder")}
                required
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold font-mono tracking-widest uppercase"
                data-testid="portal-code-input"
              />
            </div>

            {gateError && (
              <p className="text-red-600 text-sm" data-testid="portal-gate-error">
                {gateError}
              </p>
            )}

            <button
              type="submit"
              disabled={isCheckingCode || !buyerName.trim() || !accessCode.trim()}
              className="w-full flex items-center justify-center gap-2 bg-gold text-navy font-bold py-3 rounded-lg hover:bg-gold-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="portal-gate-submit"
            >
              {isCheckingCode ? t("portal.gate.checkingButton") : t("portal.gate.submitButton")}
              {!isCheckingCode && <ArrowRight size={18} />}
            </button>
          </form>

          <div className="text-center mt-6 space-y-2">
            <Link
              to={buildLocalizedPath("/projects", i18n.language)}
              className="inline-flex items-center justify-center gap-1 w-full text-slate-400 hover:text-gold text-sm transition-colors"
              data-testid="portal-projects-link"
            >
              {t("portal.gate.projectsLink")}
              <ArrowRight size={14} />
            </Link>
            <Link
              to={buildLocalizedPath("/contact", i18n.language)}
              className="inline-flex items-center justify-center gap-1 w-full text-slate-400 hover:text-gold text-sm transition-colors"
              data-testid="portal-request-access-link"
            >
              {t("portal.gate.requestAccessLink")}
              <ArrowRight size={14} />
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  // ============== STEP 3: CONFIRMATION ==============
  if (step === "success") {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-10 w-full max-w-md text-center"
        >
          <CheckCircle className="text-green-500 mx-auto mb-4" size={48} />
          <h1 className="font-outfit text-2xl font-bold text-navy" data-testid="portal-offer-success">
            {t("portal.success.title")}
          </h1>
        </motion.div>
      </div>
    );
  }

  // ============== STEP 2: LOT VIEW + OFFER FORM ==============
  return (
    <div className="min-h-screen bg-navy">
      <div className="max-w-4xl mx-auto px-4 py-16 md:py-24">
        <div className="text-center mb-10">
          <img src={logo} alt="M A Galaxy International" className="h-10 mx-auto mb-6" loading="eager" />
          <span className="text-gold text-xs uppercase tracking-[0.3em] font-semibold">{t("portal.lot.kicker")}</span>
        </div>

        {/* Lot Details */}
        <div className="bg-white rounded-2xl p-6 md:p-10 mb-8" data-testid="portal-lot-details">
          <h1 className="font-outfit text-2xl md:text-3xl font-bold text-navy mb-6">{lot.title}</h1>

          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <div className="flex items-center gap-3">
              <Package className="text-gold flex-shrink-0" size={18} strokeWidth={1.5} />
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">{t("portal.lot.brandLabel")}</p>
                <p className="text-slate-700 font-medium">{lot.brand}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Zap className="text-gold flex-shrink-0" size={18} strokeWidth={1.5} />
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">{t("portal.lot.wattageLabel")}</p>
                <p className="text-slate-700 font-medium">{lot.wattage}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Layers className="text-gold flex-shrink-0" size={18} strokeWidth={1.5} />
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">{t("portal.lot.quantityLabel")}</p>
                <p className="text-slate-700 font-medium">{lot.quantity}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <MapPin className="text-gold flex-shrink-0" size={18} strokeWidth={1.5} />
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">{t("portal.lot.locationLabel")}</p>
                <p className="text-slate-700 font-medium">{lot.location}</p>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">{t("portal.lot.conditionLabel")}</p>
            <p className="text-slate-700">{lot.condition}</p>
          </div>

          <div className="mb-8">
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">{t("portal.lot.descriptionLabel")}</p>
            <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{lot.description}</p>
          </div>

          {lot.photos && lot.photos.length > 0 && (
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-4">{t("portal.lot.photosLabel")}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {lot.photos.map((photo, index) => (
                  <img
                    key={index}
                    src={photo}
                    alt={`${lot.title} - photo ${index + 1}`}
                    className="w-full h-72 object-cover rounded-lg border border-slate-200"
                    loading="lazy"
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Offer Form */}
        <div className="bg-white rounded-2xl p-6 md:p-10">
          <h2 className="font-outfit text-xl font-bold text-navy mb-2">{t("portal.offerForm.title")}</h2>
          <p className="text-slate-500 text-sm mb-6">
            {t("portal.offerForm.subtitle")}
          </p>

          <form onSubmit={handleOfferSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{t("portal.offerForm.priceLabel")}</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={offerForm.offer_amount}
                onChange={(e) => setOfferForm({ ...offerForm, offer_amount: e.target.value })}
                placeholder={t("portal.offerForm.pricePlaceholder")}
                required
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold"
                data-testid="portal-offer-amount"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{t("portal.offerForm.quantityLabel")}</label>
              <input
                type="number"
                min="0"
                value={offerForm.quantity_wanted}
                onChange={(e) => setOfferForm({ ...offerForm, quantity_wanted: e.target.value })}
                placeholder={t("portal.offerForm.quantityPlaceholder")}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold"
                data-testid="portal-offer-quantity"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{t("portal.offerForm.messageLabel")}</label>
              <textarea
                value={offerForm.message}
                onChange={(e) => setOfferForm({ ...offerForm, message: e.target.value })}
                placeholder={t("portal.offerForm.messagePlaceholder")}
                rows={4}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold resize-y"
                data-testid="portal-offer-message"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmittingOffer || !isOfferFormValid()}
              className="w-full bg-gold text-navy font-bold py-3 rounded-lg hover:bg-gold-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="portal-offer-submit"
            >
              {isSubmittingOffer ? t("portal.offerForm.submittingButton") : t("portal.offerForm.submitButton")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TradePortalPage;
